/**
 * LLM-assisted value contestation analysis
 *
 * Synthesizes disagreeing perspectives on contested attribute values.
 * Registry: contestation_analyzer — on-demand narrator. Transport: callFeatureLlm.
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const CONTESTATION_SYSTEM_PROMPT =
  'You synthesize disagreements in prioritization value estimates to help teams resolve them. Respond with valid JSON only.';

export interface ContestationInput {
  proposedBy: string;
  value: string | number;
  confidence?: number;
  rationale?: string;
}

export interface ContestationContext {
  attributeLabel: string;
  itemTitle: string;
  inputs: ContestationInput[];
  consensusValue?: string | number;
  stdDev?: number;
}

export interface ContestationAnalysis {
  summary: string;
  positions: string[];
  overlap: string;
  crux: string;
  suggestion: string;
}

export async function analyzeContestation(
  context: ContestationContext,
): Promise<ContestationAnalysis | null> {
  if (!isAiCapabilityEnabled('contestation_analyzer') || context.inputs.length < 2) {
    return null;
  }

  try {
    const content = await callFeatureLlm({
      userPrompt: buildAnalysisPrompt(context),
      systemPrompt: CONTESTATION_SYSTEM_PROMPT,
      conversationId: 'contestation-' + Date.now(),
      model: DEFAULT_MODEL,
      temperature: 0.4,
      maxTokens: 500,
    });
    if (!content) return null;
    return parseAnalysisResponse(content);
  } catch (error) {
    console.warn('LLM contestation analysis failed:', error);
    return null;
  }
}

function buildAnalysisPrompt(context: ContestationContext): string {
  const inputList = context.inputs
    .map(
      (inp) =>
        `  - ${inp.proposedBy}: ${inp.value}${inp.confidence != null ? ` (confidence: ${Math.round(inp.confidence * 100)}%)` : ''}${inp.rationale ? ` — "${inp.rationale}"` : ''}`,
    )
    .join('\n');

  return `You are a prioritization facilitator helping a team understand why they disagree on a value estimate.

Item: "${context.itemTitle}"
Attribute being estimated: "${context.attributeLabel}"
${context.consensusValue != null ? `Current consensus (mean): ${context.consensusValue}` : ''}
${context.stdDev != null ? `Standard deviation: ${context.stdDev.toFixed(2)} (high = contested)` : ''}

Individual estimates:
${inputList}

Synthesize this disagreement. What are the distinct positions? What might explain the gap? What is the crux of the disagreement? Suggest one concrete way the team could resolve it (e.g. "clarify whether the estimate includes X" or "split into two separate attributes").

Be concise. This is shown inline next to a value row.

Return JSON only, no markdown or extra text:
{
  "summary": "One sentence describing the overall disagreement",
  "positions": ["Position A (1-2 sentences)", "Position B (1-2 sentences)"],
  "overlap": "One sentence on what the team agrees on, if anything",
  "crux": "One sentence on the core source of the disagreement",
  "suggestion": "One concrete action to resolve or clarify the disagreement"
}`;
}

function parseAnalysisResponse(content: string): ContestationAnalysis | null {
  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;

    const parsed = JSON.parse(jsonMatch[0]);
    if (typeof parsed.summary !== 'string') return null;

    return {
      summary: parsed.summary.trim(),
      positions: Array.isArray(parsed.positions)
        ? parsed.positions.filter((p: unknown) => typeof p === 'string').slice(0, 3)
        : [],
      overlap: typeof parsed.overlap === 'string' ? parsed.overlap.trim() : '',
      crux: typeof parsed.crux === 'string' ? parsed.crux.trim() : '',
      suggestion: typeof parsed.suggestion === 'string' ? parsed.suggestion.trim() : '',
    };
  } catch (e) {
    console.warn('Parse contestation analysis failed:', e);
    return null;
  }
}
